import React, { useEffect, useState } from 'react';
import { db } from '../lib/firebase';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { FileText, BookOpen, Clock, Activity, ArrowRight, Book, Zap, Trash2, Edit2, Eye, LayoutGrid, Settings } from 'lucide-react';
import { toArray } from '../lib/utils';
import { deleteDoc, doc } from 'firebase/firestore';

export function DocumentLibrary() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    const fetchDocuments = async () => {
      try {
        const q = query(
          collection(db, 'documents'),
          where('userId', '==', user.uid),
          orderBy('createdAt', 'desc')
        );
        const snap = await getDocs(q);
        const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setDocuments(docs);
      } catch (err) {
        console.error("Failed to fetch documents:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchDocuments();
  }, [user]);

  const handleCreateCourse = (docId: string) => {
    // Navigate to course creator with document ID
    navigate(`/courses/manage?documentId=${docId}`);
  };

  const handleViewBrief = (briefId: string, docId: string) => {
    // Navigate or show brief modal. We could navigate to a dedicated brief viewer or back to course creator.
    navigate(`/courses/manage?briefId=${briefId}&documentId=${docId}`);
  };
  
  const handleDelete = async (docId: string, event: React.MouseEvent) => {
     event.stopPropagation();
     if (!window.confirm("Are you sure you want to delete this document record?")) return;
     try {
       await deleteDoc(doc(db, 'documents', docId));
       setDocuments(prev => prev.filter(d => d.id !== docId));
     } catch (e) {
       console.error("Failed to delete", e);
     }
  };

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <Activity className="h-8 w-8 text-indigo-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex min-h-[calc(100vh-4rem)] bg-bg-main text-text-main">
      {/* Sidebar */}
      <div className="w-64 bg-bg-alt border-r border-border-main p-6 hidden md:block shrink-0">
        <div className="space-y-2">
          <button
            onClick={() => navigate('/courses/manage')}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-colors text-text-alt hover:bg-bg-main hover:text-text-main"
          >
            <LayoutGrid className="h-5 w-5" />
            Create Course
          </button>
          
          <div className="pt-4 pb-2">
             <div className="text-xs font-bold text-text-alt uppercase tracking-wider px-4">Manage My Courses</div>
          </div>
          
          <button
            onClick={() => navigate('/courses/manage?tab=manage')}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-colors text-text-alt hover:bg-bg-main hover:text-text-main"
          >
            <Settings className="h-5 w-5" />
            My Courses
          </button>

          <button
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-colors bg-indigo-600 text-white shadow-lg shadow-indigo-600/20"
          >
            <BookOpen className="h-5 w-5" />
            My Documents
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 overflow-y-auto w-full">
        {/* Mobile Navigation */}
        <div className="md:hidden flex gap-2 mb-6 overflow-x-auto pb-2">
          <button
            onClick={() => navigate('/courses/manage')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors bg-bg-alt text-text-alt border border-border-main hover:text-text-main"
          >
            <LayoutGrid className="h-4 w-4" />
            Create Course
          </button>
          <button
            onClick={() => navigate('/courses/manage?tab=manage')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors bg-bg-alt text-text-alt border border-border-main hover:text-text-main"
          >
            <Settings className="h-4 w-4" />
            My Courses
          </button>
          <button
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors bg-indigo-600 text-white shadow-lg shadow-indigo-600/20"
          >
            <BookOpen className="h-4 w-4" />
            My Documents
          </button>
        </div>

        <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-text-main tracking-tight flex items-center gap-3">
               <BookOpen className="h-8 w-8 text-emerald-500" />
               My Documents
            </h1>
            <p className="mt-2 text-text-alt text-lg">View uploaded documents, saved briefs, and create courses from previous uploads.</p>
          </div>
          <button
            onClick={() => navigate('/courses/manage')}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-sm transition-colors"
          >
            Upload New Document
          </button>
        </div>

      {documents.length === 0 ? (
        <div className="bg-bg-alt border border-border-main rounded-2xl p-12 text-center shadow-sm">
           <FileText className="h-12 w-12 text-text-alt opacity-50 mx-auto mb-4" />
           <h3 className="text-lg font-bold text-text-main mb-2">No documents yet.</h3>
           <p className="text-text-alt mb-6 max-w-sm mx-auto">Upload a PDF, Word document, or supported link to generate a brief or create a course.</p>
           <button
             onClick={() => navigate('/courses/manage')}
             className="px-6 py-3 text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl transition-all inline-flex items-center gap-2"
           >
             <Zap className="h-4 w-4" />
             Upload Document
           </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {documents.map((doc: any) => (
            <div key={doc.id} className="bg-bg-alt border border-border-main rounded-2xl p-6 shadow-sm hover:border-emerald-500/50 transition-all group flex flex-col">
              <div className="flex items-start justify-between mb-4">
                 <div className="h-10 w-10 bg-bg-main rounded-lg flex items-center justify-center shrink-0 border border-border-main text-emerald-500">
                   <FileText className="h-5 w-5" />
                 </div>
                 <div className="flex items-center gap-2">
                   {doc.briefId ? (
                     <span className="text-xs bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 font-bold px-2 py-1 rounded-md uppercase tracking-wider">
                       Brief Ready
                     </span>
                   ) : (
                     <span className="text-xs bg-bg-main text-text-alt border border-border-main font-bold px-2 py-1 rounded-md uppercase tracking-wider">
                       No Brief
                     </span>
                   )}
                 </div>
              </div>
              
              <h3 className="font-bold text-text-main text-lg mb-2 line-clamp-2">{doc.title}</h3>
              
              <div className="text-xs text-text-alt flex items-center gap-2 mb-4 font-mono">
                <Clock className="h-3.5 w-3.5" />
                {doc.createdAt?.toDate ? doc.createdAt.toDate().toLocaleDateString() : 'Recent'} 
                • {toArray(doc.sources).length} source(s)
              </div>
              
              <div className="mt-auto pt-4 border-t border-border-main/50 flex flex-col gap-2">
                 {doc.briefId ? (
                   <button
                     onClick={() => handleViewBrief(doc.briefId, doc.id)}
                     className="w-full flex items-center justify-center gap-2 py-2 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-500 text-sm font-bold rounded-lg transition-colors border border-emerald-500/20"
                   >
                     <Eye className="h-4 w-4" />
                     View Brief
                   </button>
                 ) : (
                   <button
                     onClick={() => handleCreateCourse(doc.id)} // Could be expanded to generate brief specifically
                     className="w-full flex items-center justify-center gap-2 py-2 hover:bg-bg-main text-text-main text-sm font-medium rounded-lg transition-colors border border-border-main"
                   >
                     Generate Brief
                   </button>
                 )}
                 <button
                   onClick={() => handleCreateCourse(doc.id)}
                   className="w-full flex items-center justify-center gap-2 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold rounded-lg transition-colors shadow-sm"
                 >
                   <Book className="h-4 w-4" />
                   Create Course
                 </button>
                 
                 <div className="flex justify-between items-center mt-2 pt-2 border-t border-border-main/50">
                    <button 
                       disabled
                       title="Ask AI Tutor - Coming Soon"
                       className="text-xs text-text-alt opacity-50 cursor-not-allowed hover:text-indigo-400 transition-colors"
                    >
                      Ask AI Tutor
                    </button>
                    <button 
                       onClick={(e) => handleDelete(doc.id, e)}
                       className="text-text-alt hover:text-rose-500 transition-colors p-1"
                       title="Delete Document"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                 </div>
              </div>
            </div>
          ))}
        </div>
      )}
      </div>
    </div>
  );
}

// Ensure you have an Eye icon import if used, or switch it. I'll use Activity
